import * as React from 'react';
import {
    ActiveConsultationDto,
    ArticleDto,
    NnSundvaegtProgram,
    ProductCardViewModel,
    ProgramFilterTagDefinition,
} from '@/features/programs/model/types';
import { CaregiverRoleEnum } from '@/shared/enums';
import { getTimedGreeting } from '@/shared/timedGreeting';
import { R } from '@/features/programs/resources/ProgramsResources.resx';
import { CheckmarkGreen, EditIcon } from '@/components/Icons';
import { ProgressDisplay } from '@/features/programs/shared/ui/ProgressDisplay';
import { Button, BackButton } from '@/components';
import { BookConsultationCard } from '@/features/programs/shared/ui/BookConsultationCard';
import { SetWeightGoalModal } from './ui/SetWeightGoalModal';
import { AddWeightLogModal } from './ui/AddWeightLogModal';
import { WeightLogCard } from './ui/WeightLogCard';
import { useNnSundVaegtProgram } from './useNnSundvaegtProgram';
import { formatNumber } from '@/shared/helpers/formatNumber';
import { LineChart } from '@/features/programs/shared/ui/LineChart';
import { Stepper } from '@/features/programs/shared/ui/Stepper';
import { format } from '@/Resources.resx';
import { UpcomingConsultation } from '@/features/programs/shared/ui/UpcomingConsultation';
import { ArticlesGrid } from '@/features/programs/shared/ui/ArticleGrid';
import { R as ProgramResources } from '@/features/programs/resources/ProgramsResources.resx';
import { FilterButton } from '@/components/buttons/FilterButtons';
import { useFilteredArticles } from '@/features/programs/shared/lib/useFilteredArticles';

export interface NnSundvaegtProgramProps {
    productCardViewModels: ProductCardViewModel[];
    nnSundvaegtProgram: NnSundvaegtProgram;
    upcomingConsultation?: ActiveConsultationDto;
    articles?: ArticleDto[];
    filterTags: ProgramFilterTagDefinition[];
}

export const NnSundVaegtProgram = ({
    productCardViewModels,
    nnSundvaegtProgram,
    upcomingConsultation,
    articles,
    filterTags,
}: NnSundvaegtProgramProps) => {
    const {
        weightLossProgress,
        weightLogCards,
        firstLog,
        showWeightGoalModal,
        openWeightGoalModal,
        closeWeightGoalModal,
        showAddWeightLogModal,
        openAddWeightLogModal,
        closeAddWeightLogModal,
        formattedExpiryDate,
        freeVideoConsultationsLeft,
        totalVideoConsultations,
        consultationsUsed,
    } = useNnSundVaegtProgram();

    const {
        selectedFilter,
        setSelectedFilter,
        filteredArticles,
        availableTagDefinitions,
    } = useFilteredArticles(articles, filterTags);

    const leftProgressDisplay = (
        <>
            <div className="text-primary-blue text-[18px] leading-[1.8rem] font-bold">
                {R.SundvaegtGoalProgressStartLabel}
            </div>
            <div className="text-primary-blue text-[14px] leading-[1.5rem]">
                {`${R.SundvaegtGoalProgressWeightLabel}: ${weightLossProgress?.initial?.weightKg ?? '--'} kg`}
            </div>
            <div className="text-primary-blue text-[14px] leading-[1rem]">
                {`BMI: ${formatNumber(weightLossProgress?.initial?.bmi) ?? '--'}`}
            </div>
        </>
    );

    const centerProgressDisplay = (
        <>
            <div className="invisible leading-[0.5rem]"></div>
            <div className="text-primary-blue text-[3rem] leading-[3rem] font-bold lg:text-[60px]">
                {`${formatNumber(weightLossProgress?.progress) ?? '-'}%`}
            </div>
            <div className="invisible leading-[1rem]"></div>
        </>
    );

    const rightProgressDisplay = (
        <>
            <div className="text-primary-blue text-[18px] leading-[1.8rem] font-bold">
                {R.SundvaegtGoalProgressEndLabel}
            </div>
            <div className="text-primary-blue text-[14px] leading-[1.5rem]">
                {`${R.SundvaegtGoalProgressWeightLabel}: ${weightLossProgress?.desired?.weightKg ?? '--'} kg`}
            </div>
            <div className="text-primary-blue text-[14px] leading-[1rem]">{`BMI: ${formatNumber(weightLossProgress?.desired?.bmi) ?? '--'}`}</div>
        </>
    );

    const footerProgressDisplay = (
        <div className="flex w-full">
            <div className="text-primary-blue text-[14px] leading-[1.8rem]">
                {weightLossProgress?.weightGoalEntered ? (
                    weightLossProgress.showProgressLabel ? (
                        <>
                            <span>
                                {
                                    R.SundvaegtWeightLogCardOverallWeightLossStatusPart1
                                }
                            </span>
                            <span className="text-[18px] font-bold">
                                {` ${weightLossProgress?.formattedTotalWeightLoss} `}
                            </span>
                            <span>
                                {
                                    R.SundvaegtWeightLogCardOverallWeightLossStatusPart2
                                }
                            </span>
                            <span>
                                {` ${weightLossProgress?.formattedWeightLossGoal}`}
                            </span>
                        </>
                    ) : (
                        R.SundvaegtProgramConfirmationLabel
                    )
                ) : null}
            </div>

            <div
                className="ml-auto flex cursor-pointer items-center text-[18px] font-bold"
                onClick={openWeightGoalModal}
            >
                <EditIcon className="cursor-pointer" />
                <span className="cursor-pointer px-2">
                    {weightLossProgress?.weightGoalEntered
                        ? R.EditGoalWeight
                        : R.SundvaegtGoalCardActionButtonText}
                </span>
            </div>
        </div>
    );

    const chartData = React.useMemo(() => {
        const logs = weightLogCards?.cards ?? [];
        return logs
            .slice()
            .reverse()
            .map((log) => ({
                x: new Date(log.Date).getTime(),
                y: log.WeightKg,
            }));
    }, [weightLogCards]);

    return (
        <div className="content-grid">
            {/* Hero + Progress display */}
            <div className="content-grid-full bg-secondary-green-extralight relative z-0 flex w-full flex-col items-center overflow-hidden md:pt-10">
                {nnSundvaegtProgram && (
                    <div className="flex-justify-center flex w-full max-w-[1400px] flex-col items-center gap-8 px-4 md:px-10 lg:h-95 lg:flex-row lg:items-end">
                        <div className="flex h-full w-full flex-col items-center justify-end lg:w-[calc(65%)] lg:pt-2">
                            <div className="w-full flex-col items-center justify-center gap-4 lg:h-95 lg:max-w-[650px] lg:min-w-[550px] lg:justify-start">
                                <div className="mx-2 mb-4 flex w-full justify-start">
                                    <BackButton />
                                </div>
                                <h4 className="text-secondary-green-dark mb-2 px-4 text-center text-[18px] lg:text-left">
                                    SundVægt Sammen
                                </h4>
                                <h2 className="px-4 text-center text-[32px] lg:text-left">
                                    {getTimedGreeting(
                                        nnSundvaegtProgram.ProfileName,
                                    )}
                                </h2>
                                <ProgressDisplay
                                    left={leftProgressDisplay}
                                    center={centerProgressDisplay}
                                    right={rightProgressDisplay}
                                    footer={footerProgressDisplay}
                                    progress={
                                        weightLossProgress?.progressNormalized ??
                                        0
                                    }
                                    progressColors={{
                                        foreground:
                                            'var(--color-secondary-green)',
                                        background:
                                            'var(--color-secondary-green-light)',
                                    }}
                                />
                            </div>
                        </div>

                        <div className="z-0 mb-[5px] flex w-full justify-center lg:w-[calc(35%)]">
                            <img
                                src={nnSundvaegtProgram.SundvaegtMainImagePath}
                                width={257}
                                height={260}
                                alt="main-illustration"
                                aria-hidden={true}
                            />
                        </div>
                    </div>
                )}

                <img
                    className="pointer-events-none absolute bottom-0 left-0 z-10 h-auto w-full"
                    src="/images/icons_shape/hero-background-mask.svg"
                    alt=""
                />
            </div>

            {upcomingConsultation && (
                <UpcomingConsultation
                    upcomingConsultation={upcomingConsultation}
                />
            )}

            <div className="content-grid-narrow my-10 justify-center">
                <h4 className="mb-6 text-center">
                    {format(
                        R.NnSundvaegtConsultationsLeft,
                        `${freeVideoConsultationsLeft}`,
                    )}
                </h4>

                <Stepper
                    steps={[...Array(totalVideoConsultations)].map(
                        (_, index) => {
                            return index + 1 > consultationsUsed
                                ? {}
                                : { icon: <CheckmarkGreen /> };
                        },
                    )}
                />

                <p className="mt-4 mb-0 text-center text-sm text-gray-800">
                    {R.NnSundvaegtConsultationsExpire}
                </p>
                <p className="my-0 text-center text-sm text-gray-800 first-letter:uppercase">
                    {formattedExpiryDate}
                </p>
            </div>

            {/* Cards */}
            <div className="flex flex-wrap items-start justify-center gap-4 px-4 md:px-10">
                {productCardViewModels.map((vm: ProductCardViewModel) => (
                    <BookConsultationCard
                        key={`${CaregiverRoleEnum[vm.CardDto.Role]}`}
                        productCard={vm}
                        showTags={true}
                        className="relative min-h-[260px] w-full max-w-[550px]"
                    />
                ))}

                {/* Weight Log Card */}
                <div className="flex min-h-[260px] w-full max-w-[550px] flex-col justify-between space-y-6 rounded-2xl border-[1px] border-gray-300 bg-white p-4">
                    <div className="text-primary mb-4 text-xl font-bold whitespace-normal">
                        {R.SundvaegtGoalCardTitle}
                    </div>
                    <div className="text-lg text-gray-900">
                        {R.SundvaegtGoalCardDescription}
                    </div>

                    <div className="mt-auto flex justify-end">
                        {weightLossProgress?.weightGoalEntered ? (
                            <Button
                                className="min-w-44"
                                variant="secondary"
                                onClick={openAddWeightLogModal}
                            >
                                {R.SundvaegtProgramLogWeightButtonLabel}
                            </Button>
                        ) : (
                            <Button
                                className="min-w-44"
                                variant="secondary"
                                onClick={openWeightGoalModal}
                            >
                                {R.SundvaegtGoalCardActionButtonText}
                            </Button>
                        )}
                    </div>

                    {weightLossProgress?.weightGoalEntered && (
                        <>
                            <LineChart data={chartData} />

                            <div className="flex w-full flex-col space-y-4">
                                {weightLogCards?.cards.map((card, index) => (
                                    <WeightLogCard
                                        card={card}
                                        key={`${card.formattedDate}${index}`}
                                        minDate={new Date(firstLog.Date)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="mt-6 flex w-full items-center justify-center md:mt-10">
                <div>{R.NnSundvaegtAreYouDone}</div>
                <a href="/nnsundvaegt/offboardingintro">
                    &nbsp;
                    <span className="cursor-pointer font-medium underline">
                        {R.NnSundvaegtCancelProgram}
                    </span>
                </a>
            </div>

            {showWeightGoalModal && (
                <SetWeightGoalModal
                    initialWeight={weightLossProgress?.initial?.weightKg}
                    height={weightLossProgress?.heightCm}
                    currentWeightGoal={weightLossProgress.desired.weightKg}
                    onClose={closeWeightGoalModal}
                />
            )}
            {showAddWeightLogModal && (
                <AddWeightLogModal
                    onClose={closeAddWeightLogModal}
                    minDate={new Date(firstLog.Date)}
                />
            )}
            <div className="relative right-1/2 left-1/2 mt-10 w-screen -translate-x-1/2 bg-gray-100">
                <div className="mx-auto max-w-[1440px] px-4 md:px-10">
                    <p className="text-font-body pt-10 font-bold text-gray-800">
                        {ProgramResources.ArticlesHeader}
                    </p>

                    <div className="mb-6 flex flex-wrap justify-start gap-4">
                        <FilterButton
                            text={ProgramResources.All}
                            isSelected={selectedFilter === null}
                            onClick={() => setSelectedFilter(null)}
                        />

                        {availableTagDefinitions.map((tagDef) => (
                            <FilterButton
                                key={tagDef.Tag}
                                text={tagDef.Name}
                                isSelected={selectedFilter === tagDef.Tag}
                                onClick={() => setSelectedFilter(tagDef.Tag)}
                            />
                        ))}
                    </div>

                    <ArticlesGrid articles={filteredArticles} />
                </div>
            </div>
        </div>
    );
};
